import React from 'react';
import { ArrowRight, Check } from 'lucide-react';

const badgeStyles = {
    plasma: 'bg-[#7B61FF] text-[#F0EFF4] border-[#7B61FF]',
    graphite: 'bg-[#18181B] text-[#F0EFF4] border-[#F0EFF4]/20',
};

const ctaStyles = {
    plasma:
        'bg-[#7B61FF] text-[#F0EFF4] shadow-[0_0_20px_rgba(123,97,255,0.4)] hover:shadow-[0_0_40px_rgba(123,97,255,0.65)]',
    outline:
        'border border-[#F0EFF4]/30 text-[#F0EFF4] hover:bg-[#F0EFF4]/5',
};

const PricingCard = ({
    badge,
    badgeVariant = 'graphite',
    price,
    priceNote,
    headline,
    subtext,
    items = [],
    itemsLabel,
    ctaLabel,
    ctaHref,
    ctaVariant = 'plasma',
    ctaTarget,
    footnote,
    offerNote,
    highlighted = false,
}) => {
    return (
        <div
            className={`relative flex flex-col h-full rounded-[2.5rem] p-8 md:p-10 bg-[#18181B] border transition-shadow ${
                highlighted
                    ? 'border-[#7B61FF]/50 shadow-[0_0_40px_rgba(123,97,255,0.15)]'
                    : 'border-[#F0EFF4]/10'
            }`}
        >
            {highlighted ? (
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#7B61FF]/15 to-transparent rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
            ) : null}

            <div className="relative z-10 flex flex-col h-full">
                {badge ? (
                    <span
                        className={`self-start font-data text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border ${badgeStyles[badgeVariant]}`}
                    >
                        {badge}
                    </span>
                ) : null}

                <div className="mt-6 mb-2">
                    <p className="font-drama text-[#F0EFF4] text-5xl md:text-6xl leading-none">
                        {price}
                    </p>
                    {priceNote ? (
                        <p className="font-data text-[#F0EFF4]/40 text-xs tracking-widest uppercase mt-2">
                            {priceNote}
                        </p>
                    ) : null}
                </div>

                <h3 className="text-[#F0EFF4] font-bold text-2xl md:text-3xl tracking-tight mt-6 mb-3">
                    {headline}
                </h3>
                <p className="text-[#F0EFF4]/60 text-base leading-relaxed mb-8">
                    {subtext}
                </p>

                {itemsLabel ? (
                    <p className="font-data text-[#F0EFF4]/40 text-xs tracking-widest uppercase mb-4">
                        {itemsLabel}
                    </p>
                ) : null}

                <ul className="flex flex-col gap-3 mb-8">
                    {items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <Check size={16} className="text-[#7B61FF] shrink-0 mt-[3px]" />
                            <span className="text-[#F0EFF4]/80 text-sm leading-relaxed">
                                {item}
                            </span>
                        </li>
                    ))}
                </ul>

                {offerNote ? (
                    <div className="mb-6 rounded-2xl border border-[#7B61FF]/30 bg-[#7B61FF]/5 p-4">
                        <p className="text-[#F0EFF4]/80 text-sm leading-relaxed">
                            {offerNote}
                        </p>
                    </div>
                ) : null}

                <div className="mt-auto pt-2">
                    <a
                        href={ctaHref}
                        target={ctaTarget}
                        rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
                        className={`btn-magnetic w-full flex items-center justify-center gap-3 px-6 py-4 rounded-full font-semibold text-base tracking-wide transition-all ${ctaStyles[ctaVariant]}`}
                    >
                        {ctaLabel} <ArrowRight size={18} />
                    </a>
                    {footnote ? (
                        <p className="text-[#F0EFF4]/40 text-xs mt-4 text-center font-data tracking-wide">
                            {footnote}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default PricingCard;
